import { useEffect, useId, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { useAuth } from '../../auth/useAuth'
import type { ConnectionStatus } from '../../stream/sse'

interface TopbarProps {
  /** The engagement id, when the bar is above one. Absent on the workspace. */
  engagementId?: string

  /** Live / reconnecting / signed out, from the stream this page holds. */
  connection: ConnectionStatus

  /** True while a silent token refresh is failing. */
  stale?: boolean
}

/**
 * THE TOP BAR, from `Frontend/workspace.html` and `Frontend/engagement.html`.
 *
 * <p>The markup is the mockups' own: `.topbar`, `.product`, `.crumb`, `.conn[data-state]`,
 * `.usermenu` with its panel, and the empty `.brandmark` slot the design system reserves for
 * master artwork (`§6.5`, `§14`). Both files carry the same bar, so it is one component here.</p>
 *
 * <p><b>THE CONNECTION STATE IS A `data-state`, NOT A LABEL.</b> `eia-brand.css` renders the word
 * through `::after` and the dot through a background colour, so the text and the colour cannot
 * disagree — which is what happens when a component writes both.</p>
 */
export function Topbar({ engagementId, connection, stale = false }: TopbarProps)
  : React.JSX.Element {
  const { account, signOut } = useAuth()
  const [open, setOpen] = useState(false)
  const menuId = useId()
  const buttonId = useId()
  const wrap = useRef<HTMLDivElement | null>(null)

  // A MENU THAT CANNOT BE DISMISSED IS A TRAP. Escape and a click outside both close it, and the
  // focus stays where the reader left it.
  useEffect(() => {
    if (!open) return

    const away = (event: MouseEvent) => {
      if (wrap.current && !wrap.current.contains(event.target as Node)) setOpen(false)
    }
    const escape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false)
    }

    document.addEventListener('mousedown', away)
    document.addEventListener('keydown', escape)
    return () => {
      document.removeEventListener('mousedown', away)
      document.removeEventListener('keydown', escape)
    }
  }, [open])

  const name = account?.name ?? 'Signed in'
  const upn = account?.username ?? ''

  // THE DESIGN SYSTEM'S THREE STATES, not the transport's five. `eia-brand.css` renders the WORD
  // through `::after` and the dot through a background colour, keyed on `data-state` — so a value
  // it does not know renders a grey dot with NO LABEL AT ALL, which is what `open` did on the
  // deployed page. The mapping belongs here because the transport's vocabulary is the transport's.
  // A FIRST CONNECTION IS NOT A RECONNECTION. `connecting` says "wait" without asserting that
  // something was established and lost, which on a first load is false — and which is the one
  // distinction this indicator exists to draw.
  const state = stale ? 'signedout'
    : connection === 'open' ? 'connected'
    : connection === 'connecting' ? 'connecting'
    : 'reconnecting'

  const bar = (
    <>
      <span className="product">Economic Impact Advisor</span>

      {engagementId !== undefined && (
        <nav className="crumb" aria-label="Breadcrumb">
          <span className="sep" aria-hidden="true">/</span>
          <Link to="/">Engagements</Link>
          <span className="sep" aria-hidden="true">/</span>
          <span className="here">{engagementId}</span>
        </nav>
      )}

      <div className="spacer" />

      <div className="conn" data-state={state} role="status" aria-live="polite"
           aria-label={`Connection: ${state}`}>
        <span className="dot" />
      </div>

      <div className="account" ref={wrap}>
        <button className="usermenu" id={buttonId} aria-haspopup="menu"
                aria-expanded={open} aria-controls={menuId}
                onClick={() => setOpen((was) => !was)}>
          <span className="avatar" aria-hidden="true">{initials(name)}</span>
          <span className="uname">{name}</span>
          <span className="caret" aria-hidden="true">▾</span>
        </button>

        <div className="usermenu-panel" id={menuId} role="menu" aria-labelledby={buttonId}
             hidden={!open}>
          <div className="who">
            <span className="nm">{name}</span>
            <span className="upn">{upn}</span>
          </div>
          <button role="menuitem" onClick={() => void signOut()}>Sign out</button>

          {/* THE ONE THING A READER NEEDS TO KNOW BEFORE SIGNING OUT. An engagement is a durable
              orchestration; closing the tab does not stop it, and a person who believes it might
              will sit and watch a gate instead of going home. */}
          <p className="note">
            Signing out does not stop an engagement. Anything waiting for you will still be there
            when you sign back in.
          </p>
        </div>
      </div>

      {/* The ADDED horizontal lockup's slot. §14: master artwork only, so it stays empty here. */}
      <div className="brandmark" />
    </>
  )

  // The workspace wraps the bar in `.bar-inner` to centre it on the page measure; the engagement
  // runs it full width, because the rail and the drawer already bound the layout.
  return (
    <header className="topbar">
      {engagementId === undefined ? <div className="bar-inner">{bar}</div> : bar}
    </header>
  )
}

/** Two letters, from the display name. The avatar is decorative and the name is beside it. */
function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter((p) => p.length > 0)
  if (parts.length === 0) return '·'
  if (parts.length === 1) return (parts[0] ?? '').slice(0, 2).toUpperCase()
  return ((parts[0] ?? '')[0] ?? '').toUpperCase() + ((parts[parts.length - 1] ?? '')[0] ?? '').toUpperCase()
}
